import React, { useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const items = [{
  emoji: '📖',
  pages: '360+ Pages',
  title: 'The 6 Volumes',
  desc: 'Monthly planning with technical illustrations and clear goals.'
}, {
  emoji: '🧘',
  pages: '60+ Pages',
  title: 'The Teacher Brand Manual',
  desc: 'How to create your unique Aussie style and stand out in a saturated market.'
}, {
  emoji: '🗂️',
  pages: '40+ Pages',
  title: 'Quick Reference Cards',
  desc: 'Visual cards for your mat side for an instant cue check.'
}, {
  emoji: '📋',
  pages: '50+ Pages',
  title: 'Student Registry & Intake',
  desc: 'Track your students’ progress like a total pro.'
}];
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/5KcqQ659XroAZv3kkRUVXx/Captura_de_pantalla_2026-05-14_a_las_6.29.09_p.webp',
  alt: 'Before Class Starts: Read The Room — Pre-class checklist for Australian studios'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6HFdcZPPwrdHvxgySGdkxd/Captura_de_pantalla_2026-05-14_a_las_6.29.42_p.webp',
  alt: 'Sequence 04: Hip Opening Deep Dive — Why hip opening matters in Australian yoga classes'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6Di6vxGRwgddNEPYZWARNt/Captura_de_pantalla_2026-05-14_a_las_6.30.16_p.webp',
  alt: 'Sequence 05: The Athlete’s Yin — Hip and lower back recovery for active Australians'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vzWF7W5AbAEbPYpMavwEFm/Captura_de_pantalla_2026-05-14_a_las_6.30.47_p.webp',
  alt: '30 Ready-to-Use Instagram Captions — Organised by purpose for yoga teachers'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nhMQrDvSVBPcWQojZNDHe2/Captura_de_pantalla_2026-05-14_a_las_6.31.32_p.webp',
  alt: 'Sport-Specific Yoga Prescription Guide — Quick reference for every Australian sport'
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const center = container.scrollLeft + container.clientWidth / 2;
    let minDistance = Infinity;
    let active = 0;
    Array.from(container.children).forEach((child, index) => {
      const childCenter = (child as HTMLElement).offsetLeft + (child as HTMLElement).clientWidth / 2;
      const distance = Math.abs(center - childCenter);
      if (distance < minDistance) {
        minDistance = distance;
        active = index;
      }
    });
    setActiveIndex(active);
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => {
    if (activeIndex > 0) scrollToIndex(activeIndex - 1);
  };
  const scrollNext = () => {
    if (activeIndex < carouselImages.length - 1) scrollToIndex(activeIndex + 1);
  };
  return <section className="w-full bg-bg-dark py-20 px-4" data-id="element-26">
      <div className="max-w-5xl mx-auto" data-id="element-27">
        <div className="text-center mb-16" data-id="element-28">
          <h2 className="text-3xl sm:text-5xl font-black text-text-main mb-4 tracking-tight" data-id="element-29">
            📦 What you receive instantly:
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto" data-id="element-30">
            <span className="text-primary font-bold" data-id="element-31">
              Over 480 pages of ready-to-use material.
            </span>{' '}
            Professional PDFs optimized for digital use or HQ printing.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8" data-id="element-32">
          {items.map((item, i) => <div key={i} className="bg-bg-alt rounded-2xl border border-border-light overflow-hidden flex flex-col relative group hover:-translate-y-1 hover:border-primary/40 transition-all duration-300" data-id="element-33">
              <div className="bg-primary h-1.5 w-full shadow-[0_0_15px_rgba(168,85,247,0.5)]" data-id="element-34" />
              <div className="flex-1 flex flex-col p-8 relative" data-id="element-35">
                <div className="absolute inset-0 bg-primary opacity-[0.03] group-hover:opacity-[0.08] transition-opacity" data-id="element-36"></div>

                <span className="text-5xl mb-3 block drop-shadow-2xl relative z-10 group-hover:scale-110 transition-transform duration-300" data-id="element-37">
                  {item.emoji}
                </span>
                <span className="inline-block self-start text-primary font-black text-xs sm:text-sm bg-primary/10 px-3 py-1 rounded-full border border-primary/20 mb-3 relative z-10" data-id="element-38">
                  {item.pages}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-text-main leading-snug relative z-10 mb-3" data-id="element-39">
                  {item.title}
                </h3>
                <p className="text-text-sec text-base leading-relaxed relative z-10" data-id="element-40">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </div>

        {/* Carousel Section */}
        <div className="mt-24 relative" data-id="element-41">
          <div className="text-center mb-10" data-id="element-42">
            <h3 className="text-2xl sm:text-3xl font-bold text-text-main inline-flex items-center gap-3" data-id="element-43">
              <span data-id="element-44">📸</span> See the material from the inside
            </h3>
            <p className="text-text-sec text-sm mt-3 flex items-center justify-center gap-2" data-id="element-45">
              <span className="inline-flex items-center gap-1.5 sm:hidden" data-id="element-46">
                <ChevronLeft className="w-4 h-4 animate-pulse" data-id="element-47" />
                Swipe for more
                <ChevronRight className="w-4 h-4 animate-pulse" data-id="element-48" />
              </span>
              <span className="text-text-sec text-xs font-medium" data-id="element-49">
                {activeIndex + 1} / {carouselImages.length}
              </span>
            </p>
          </div>

          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-50">
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-white border border-border-light p-3 rounded-full text-text-main backdrop-blur-sm transition-all shadow-lg ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Previous" data-id="element-51">
              <ChevronLeft className="w-6 h-6" data-id="element-52" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-white border border-border-light p-3 rounded-full text-text-main backdrop-blur-sm transition-all shadow-lg ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Next" data-id="element-53">
              <ChevronRight className="w-6 h-6" data-id="element-54" />
            </button>

            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-175px)] pb-8 pt-4" data-id="element-55">
              {carouselImages.map((img, i) => <div key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[350px] bg-bg-alt border border-border-light rounded-2xl overflow-hidden p-2 sm:p-3 flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(90,70,120,0.25)] transition-transform duration-300 ${activeIndex === i ? 'scale-100 opacity-100' : 'scale-95 opacity-50'}`} data-id="element-56">
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[400px] sm:max-h-[450px] object-contain rounded-xl bg-white" data-id="element-57" />
                </div>)}
            </div>
          </div>

          <div className="flex justify-center gap-1 mt-2" data-id="element-58">
            {carouselImages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className="p-3 flex items-center justify-center" aria-label={`Go to image ${i + 1}`} data-id="element-59">
                <span className={`block h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8' : 'bg-border-light hover:bg-text-sec w-2'}`} data-id="element-60" />
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}